import React, { useEffect, useState, useRef, memo, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, XIcon, AlertTriangleIcon, CheckIcon, CheckCircleIcon, UsersIcon, UtensilsIcon, FlameIcon, HeartPulseIcon, BookOpenIcon, ShieldCheckIcon, ChevronDownIcon, XCircleIcon, ClockIcon, ActivityIcon, LeafIcon } from 'lucide-react';
// --- UTILS & COOKIES ---
const setCookie = (name: string, value: string, days: number) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = '; expires=' + date.toUTCString();
  document.cookie = name + '=' + value + expires + '; path=/';
};
const getCookie = (name: string) => {
  if (typeof document === 'undefined') return null;
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
// --- META PIXEL ---
function MetaPixel() {
  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const timeoutId = setTimeout(() => {
      if (!window.fbq) {
        window.fbq = function () {
          window.fbq.callMethod ? window.fbq.callMethod.apply(window.fbq, arguments) : window.fbq.queue.push(arguments);
        };
        if (!window._fbq) window._fbq = window.fbq;
        window.fbq.push = window.fbq;
        window.fbq.loaded = true;
        window.fbq.version = '2.0';
        window.fbq.queue = [];
        const script = document.createElement('script');
        script.async = true;
        script.defer = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        document.head.appendChild(script);
        window.fbq('init', '1641522006892249');
        window.fbq('track', 'PageView');
      }
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, []);
  return null;
}
// --- SHARED COMPONENTS ---
const scrollToPricing = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
  e.preventDefault();
  document.getElementById('comprar')?.scrollIntoView({
    behavior: 'smooth'
  });
};
function ScrollButton({
  className = ''
}: {
  className?: string;
}) {
  return <div className="flex justify-center w-full" data-id="element-0">
      <button onClick={scrollToPricing} className={`bg-orange-700 hover:bg-orange-800 text-white font-bold px-6 py-3 rounded-lg text-base md:text-lg transition-all duration-300 shadow-xl transform hover:scale-105 active:scale-95 flex items-center justify-center ${className}`} data-id="element-1">
        Quiero el protocolo <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-2" />
      </button>
    </div>;
}
function LiveVisitorCounter() {
  return <div className="flex items-center text-sm font-medium text-orange-800 bg-orange-100 py-1.5 px-4 rounded-full animate-pulse w-fit border border-orange-200" data-id="element-3">
      <UsersIcon className="h-4 w-4 mr-2" data-id="element-4" />
      <span data-id="element-5">Hay personas viendo esta página ahora mismo</span>
    </div>;
}
const CountdownBadge = memo(function CountdownBadge() {
  const [copiesLeft, setCopiesLeft] = useState(() => {
    if (typeof document !== 'undefined') {
      const storedCount = getCookie('copiesLeft');
      return storedCount ? parseInt(storedCount) : 47;
    }
    return 47;
  });
  useEffect(() => {
    if (!getCookie('copiesLeft')) {
      setCookie('copiesLeft', '47', 30);
    }
    let intervalId: any;
    const timeoutId = setTimeout(() => {
      intervalId = setInterval(() => {
        setCopiesLeft(prev => {
          const newCount = prev > 3 ? prev - 1 : 3;
          setCookie('copiesLeft', newCount.toString(), 30);
          return newCount;
        });
      }, 8000);
    }, 3000);
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);
  return <div className="bg-red-100 text-red-800 font-bold px-5 py-2.5 rounded-lg shadow-sm border border-red-200 animate-pulse w-fit mx-auto flex items-center" data-id="element-6">
      <FlameIcon className="h-5 w-5 mr-2 text-red-600" data-id="element-7" />
      Solo {copiesLeft} copias disponibles a este precio
    </div>;
});
// --- SECTIONS ---
function Header() {
  return <header className="bg-emerald-900 py-4 md:py-5 px-5 md:px-8 sticky top-0 z-50 shadow-xl border-b border-emerald-800" data-id="element-8">
      <div className="max-w-7xl mx-auto flex justify-between items-center gap-6" data-id="element-9">
        {/* Left: Button */}
        <button onClick={scrollToPricing} className="bg-orange-600 hover:bg-orange-500 text-white font-semibold px-3 py-1.5 md:px-4 md:py-2 rounded-md transition-all duration-300 shadow-md text-xs md:text-sm transform hover:scale-105 active:scale-95" data-id="element-10">
          Quiero el protocolo
        </button>

        {/* Right: Logo */}
        <div className="text-stone-100 font-bold text-xl md:text-2xl tracking-wide flex items-center" data-id="element-11">
          EL PROTOCOLO
          <LeafIcon className="ml-2 h-5 w-5 text-emerald-400" data-id="element-12" />
        </div>
      </div>
    </header>;
}
function HeroSection() {
  return <section className="bg-stone-50 py-12 md:py-20 relative overflow-hidden" data-id="element-13">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 opacity-5 pointer-events-none" data-id="element-14">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-orange-900 rounded-full blur-3xl" data-id="element-15"></div>
        <div className="absolute top-1/2 -left-24 w-64 h-64 bg-emerald-900 rounded-full blur-3xl" data-id="element-16"></div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 relative z-10" data-id="element-17">
        <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16" data-id="element-18">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} className="md:w-1/2 space-y-6" data-id="element-19">
            <div className="inline-flex items-center text-sm font-bold text-orange-800 bg-orange-100 px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-20">
              <HeartPulseIcon className="h-4 w-4 mr-2" data-id="element-21" /> Para Diabéticos y
              Pre-diabéticos
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-stone-900 leading-tight text-center" data-id="element-22">
              El Protocolo de la{' '}
              <span className="text-orange-700" data-id="element-23">Antojería Diabética</span>
            </h1>
            <h2 className="text-xl md:text-2xl font-medium text-stone-600 font-serif italic" data-id="element-24">
              Vuelve a comer Tacos, Pozole y Antojitos Mexicanos sin que tu
              glucómetro te castigue.
            </h2>

            <div className="bg-white border-l-4 border-orange-600 p-5 shadow-sm rounded-r-lg my-6" data-id="element-25">
              <p className="text-base md:text-lg text-stone-700 leading-relaxed" data-id="element-26">
                <span className="font-bold text-stone-900" data-id="element-27">
                  Es domingo por la tarde.
                </span>{' '}
                El olor a carne asada y salsa fresca llena la casa. Tu familia
                ríe mientras se sirve la tercera orden de tacos, pero tú tienes
                frente a ti un plato de pechuga asada fría y una ensalada sin
                sabor. Esa exclusión duele más que el pinchazo en el dedo.
              </p>
            </div>

            <p className="text-base md:text-lg text-stone-600" data-id="element-28">
              Te han dicho que para controlar tu diabetes tienes que renunciar a
              tu cultura, a tus raíces y al placer de sentarte a la mesa como
              uno más.{' '}
              <span className="font-bold text-orange-700" data-id="element-29">Te han mentido.</span>
            </p>

            <div className="pt-4" data-id="element-30">
              <ScrollButton data-id="element-31" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="md:w-1/2 relative" data-id="element-32">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white" data-id="element-33">
              <img src="https://cdn.magicpatterns.com/uploads/uwAMdYmMkAw1QTfShAgz8H/Hyper-realistic_professional_digital_202604222135.jpg" alt="El Protocolo de la Antojería Diabética" className="w-full h-auto object-cover" data-id="element-34" />
              <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm text-stone-900 px-4 py-2 rounded-lg shadow-lg flex flex-col items-center border border-stone-200" data-id="element-35">
                <span className="font-bold text-sm uppercase tracking-wide text-orange-700" data-id="element-36">
                  Oferta Especial
                </span>
                <span className="font-bold text-xl" data-id="element-37">$11.99 USD</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function ProblemSection() {
  return <section className="py-16 md:py-24 bg-white" data-id="element-38">
      <div className="max-w-7xl mx-auto px-5 md:px-8" data-id="element-39">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12 md:mb-16" data-id="element-40">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-stone-900 mb-6" data-id="element-41">
            La dieta "de hospital" es una sentencia de aislamiento
          </h2>
          <p className="text-lg md:text-xl text-stone-600 max-w-4xl mx-auto leading-relaxed" data-id="element-42">
            El sistema de salud tradicional te entrega una lista de "alimentos
            prohibidos" que parece una sentencia de aislamiento social. Te
            obligan a comer avena y pan integral que saben a cartón, ignorando
            que tu cuerpo pide el picante, la textura y el alma de la cocina
            mexicana.
          </p>
        </motion.div>

        <div className="mb-16 rounded-2xl overflow-hidden shadow-2xl border-4 border-stone-100" data-id="element-43">
          <img src="https://cdn.magicpatterns.com/uploads/cC1iCXU82L8ZHvJ2nnKZE2/Crea_una_imagen_202604222149.jpg" alt="Contraste entre dieta aburrida y disfrutar en familia" className="w-full h-auto object-cover max-h-[600px]" data-id="element-44" />
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12" data-id="element-45">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-stone-50 p-8 rounded-2xl border border-stone-200 shadow-sm relative overflow-hidden" data-id="element-46">
            <div className="absolute top-0 left-0 w-2 h-full bg-red-500" data-id="element-47"></div>
            <h3 className="text-xl md:text-2xl font-serif font-bold text-stone-900 mb-6 flex items-center" data-id="element-48">
              <XCircleIcon className="h-6 w-6 text-red-500 mr-3" data-id="element-49" /> Lo que todos
              hacen mal
            </h3>
            <ul className="space-y-4" data-id="element-50">
              <li className="flex items-start text-stone-700" data-id="element-51">
                <span className="mr-3 mt-1 text-red-400" data-id="element-52">•</span> Compran
                tortillas "ligeras" del súper que siguen teniendo hasta un 70%
                de harina procesada.
              </li>
              <li className="flex items-start text-stone-700" data-id="element-53">
                <span className="mr-3 mt-1 text-red-400" data-id="element-54">•</span> Comen solo "el
                relleno" con tenedor, quedando psicológicamente hambrientos.
              </li>
              <li className="flex items-start text-stone-700" data-id="element-55">
                <span className="mr-3 mt-1 text-red-400" data-id="element-56">•</span> Sustituyen el
                azúcar por edulcorantes artificiales que no detienen el deseo de
                carbohidratos reales.
              </li>
              <li className="flex items-start text-stone-700" data-id="element-57">
                <span className="mr-3 mt-1 text-red-400" data-id="element-58">•</span> Siguen dietas
                genéricas que no entienden que en México, la comida es familia.
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-stone-50 p-8 rounded-2xl border border-stone-200 shadow-sm relative overflow-hidden" data-id="element-59">
            <div className="absolute top-0 left-0 w-2 h-full bg-orange-600" data-id="element-60"></div>
            <h3 className="text-xl md:text-2xl font-serif font-bold text-stone-900 mb-6 flex items-center" data-id="element-61">
              <AlertTriangleIcon className="h-6 w-6 text-orange-600 mr-3" data-id="element-62" /> El
              resultado inevitable
            </h3>
            <p className="text-stone-700 mb-4 leading-relaxed" data-id="element-63">
              El problema no es la comida mexicana. El problema es que nadie te
              ha enseñado a separar el sabor que amas del veneno que dispara tu
              insulina.
            </p>
            <p className="text-stone-700 font-medium leading-relaxed" data-id="element-64">
              Seguir una dieta "de hospital" no solo es aburrido, es
              insostenible, y es la razón principal por la que terminas pecando
              y sufriendo picos de glucosa peligrosos a las 11 de la noche.
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}
function UniqueMethodSection() {
  const cards = [{
    title: 'Identifica',
    desc: 'Localiza el carbohidrato oculto en tu platillo favorito sin sacrificar el sabor principal.',
    icon: <ActivityIcon className="h-8 w-8 text-emerald-600" data-id="element-65" />
  }, {
    title: 'Intercambia',
    desc: 'Sustituye el vehículo (masa/arroz) por nuestra opción de fibra o proteína biomimética.',
    icon: <UtensilsIcon className="h-8 w-8 text-emerald-600" data-id="element-66" />
  }, {
    title: 'Mide',
    desc: 'Comprueba en tu glucómetro cómo el nivel se mantiene estable mientras disfrutas tu cena.',
    icon: <HeartPulseIcon className="h-8 w-8 text-emerald-600" data-id="element-67" />
  }];
  return <section className="py-16 md:py-24 bg-emerald-900 text-stone-100 relative overflow-hidden" data-id="element-68">
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-emerald-600 via-transparent to-transparent" data-id="element-69"></div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 relative z-10" data-id="element-70">
        <div className="text-center mb-16" data-id="element-71">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6" data-id="element-72">
            El Método del Intercambio Inteligente
          </h2>
          <p className="text-lg md:text-xl text-emerald-100 max-w-3xl mx-auto leading-relaxed" data-id="element-73">
            Este protocolo no elimina el antojito,{' '}
            <span className="font-bold text-orange-400" data-id="element-74">
              cambia el vehículo
            </span>
            . Mediante la sustitución biomimética, aprendes a usar bases de
            fibra y proteína (como el nopal baby o la costra de queso artesanal)
            que imitan la función de la tortilla pero mantienen tu curva de
            glucosa plana.
          </p>
          <p className="text-lg md:text-xl text-emerald-100 max-w-3xl mx-auto mt-4 font-medium" data-id="element-75">
            Disfrutas del mismo adobo, la misma salsa y la misma satisfacción
            táctil, mientras tu páncreas descansa.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12" data-id="element-76">
          {cards.map((card, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.2
        }} className="bg-stone-50 rounded-2xl p-8 text-center shadow-xl border border-emerald-800 transform transition hover:-translate-y-2" data-id="element-77">
              <div className="inline-flex items-center justify-center p-4 bg-emerald-100 rounded-full mb-6" data-id="element-78">
                {card.icon}
              </div>
              <h3 className="text-2xl font-serif font-bold text-stone-900 mb-4" data-id="element-79">
                {card.title}
              </h3>
              <p className="text-stone-600 leading-relaxed" data-id="element-80">{card.desc}</p>
            </motion.div>)}
        </div>

        <div className="flex justify-center mt-12" data-id="element-81">
          <ScrollButton className="bg-orange-600 hover:bg-orange-500 text-white" data-id="element-82" />
        </div>
      </div>
    </section>;
}
function WhatYouGetSection() {
  const items = [{
    title: 'El Protocolo Maestro',
    desc: 'La estrategia paso a paso para hackear los 20 antojitos más populares de México.',
    icon: <BookOpenIcon data-id="element-83" />
  }, {
    title: 'Recetario de Rescate',
    desc: 'Pozole de coliflor, tamales de nube y tacos de impacto cero que engañan a cualquiera.',
    icon: <UtensilsIcon data-id="element-84" />
  }, {
    title: 'El Semáforo de la Garnacha',
    desc: 'Tu guía visual para saber exactamente qué pedir en un puesto callejero y qué evitar a toda costa.',
    icon: <ActivityIcon data-id="element-85" />
  }, {
    title: 'Manual de Salsas que Sanan',
    desc: 'Cómo usar el chile y el vinagre de manzana para mejorar tu sensibilidad a la insulina.',
    icon: <FlameIcon data-id="element-86" />
  }];
  return <section className="py-16 md:py-24 bg-stone-100" data-id="element-87">
      <div className="max-w-7xl mx-auto px-5 md:px-8" data-id="element-88">
        <div className="text-center mb-16" data-id="element-89">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-stone-900 mb-4" data-id="element-90">
            Lo que obtienes hoy
          </h2>
          <p className="text-lg text-stone-600" data-id="element-91">
            Todo lo que necesitas para transformar tu alimentación sin perder el
            sabor.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8" data-id="element-92">
          {items.map((item, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          x: idx % 2 === 0 ? -20 : 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white p-6 md:p-8 rounded-2xl shadow-md border border-stone-200 flex items-start" data-id="element-93">
              <div className="bg-orange-100 p-3 rounded-xl text-orange-700 mr-5 flex-shrink-0" data-id="element-94">
                {item.icon}
              </div>
              <div data-id="element-95">
                <h3 className="text-xl font-serif font-bold text-stone-900 mb-2" data-id="element-96">
                  {item.title}
                </h3>
                <p className="text-stone-600 leading-relaxed" data-id="element-97">{item.desc}</p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
function WhoIsThisForSection() {
  return <section className="py-16 md:py-24 bg-white" data-id="element-98">
      <div className="max-w-7xl mx-auto px-5 md:px-8" data-id="element-99">
        <div className="grid md:grid-cols-2 gap-12" data-id="element-100">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-emerald-50 p-8 md:p-10 rounded-3xl border border-emerald-100" data-id="element-101">
            <h3 className="text-2xl md:text-3xl font-serif font-bold text-emerald-900 mb-8 flex items-center" data-id="element-102">
              <CheckCircleIcon className="h-8 w-8 text-emerald-600 mr-3" data-id="element-103" /> Esto
              SÍ es para ti si...
            </h3>
            <ul className="space-y-6" data-id="element-104">
              <li className="flex items-start" data-id="element-105">
                <CheckIcon className="h-6 w-6 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-106" />
                <span className="text-stone-700 text-lg" data-id="element-107">
                  Eres un mexicano con Diabetes Tipo 2 que extraña su comida
                  tradicional.
                </span>
              </li>
              <li className="flex items-start" data-id="element-108">
                <CheckIcon className="h-6 w-6 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-109" />
                <span className="text-stone-700 text-lg" data-id="element-110">
                  Tienes Pre-diabetes y quieres evitar la medicación sin dejar
                  de comer rico.
                </span>
              </li>
              <li className="flex items-start" data-id="element-111">
                <CheckIcon className="h-6 w-6 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-112" />
                <span className="text-stone-700 text-lg" data-id="element-113">
                  Eres familiar de un diabético y quieres cocinar algo que todos
                  disfruten por igual.
                </span>
              </li>
              <li className="flex items-start" data-id="element-114">
                <CheckIcon className="h-6 w-6 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-115" />
                <span className="text-stone-700 text-lg" data-id="element-116">
                  Amas la comida mexicana y buscas una opción baja en
                  carbohidratos (Keto-Friendly).
                </span>
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-red-50 p-8 md:p-10 rounded-3xl border border-red-100" data-id="element-117">
            <h3 className="text-2xl md:text-3xl font-serif font-bold text-red-900 mb-8 flex items-center" data-id="element-118">
              <XCircleIcon className="h-8 w-8 text-red-500 mr-3" data-id="element-119" /> Esto NO es
              para ti si...
            </h3>
            <ul className="space-y-6" data-id="element-120">
              <li className="flex items-start" data-id="element-121">
                <XIcon className="h-6 w-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-122" />
                <span className="text-stone-700 text-lg" data-id="element-123">
                  Buscas una "cura milagrosa" sin estar dispuesto a cambiar tus
                  ingredientes básicos.
                </span>
              </li>
              <li className="flex items-start" data-id="element-124">
                <XIcon className="h-6 w-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-125" />
                <span className="text-stone-700 text-lg" data-id="element-126">
                  Prefieres seguir comiendo harina refinada y esperar que la
                  insulina haga todo el trabajo.
                </span>
              </li>
              <li className="flex items-start" data-id="element-127">
                <XIcon className="h-6 w-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-128" />
                <span className="text-stone-700 text-lg" data-id="element-129">
                  No disfrutas del sabor, las especias y la riqueza de la
                  gastronomía de México.
                </span>
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
    </section>;
}
function ProofSection() {
  return <section className="py-16 md:py-24 bg-stone-900 text-white relative" data-id="element-130">
      <div className="max-w-5xl mx-auto px-5 md:px-8 text-center" data-id="element-131">
        <ShieldCheckIcon className="h-12 w-12 text-orange-500 mx-auto mb-6" data-id="element-132" />
        <h2 className="text-3xl md:text-5xl font-serif font-bold mb-8" data-id="element-133">
          El Cambio de los 7 Días
        </h2>

        <div className="bg-stone-800/50 p-8 md:p-12 rounded-3xl border border-stone-700 backdrop-blur-sm" data-id="element-134">
          <p className="text-xl md:text-2xl text-stone-300 italic leading-relaxed mb-8" data-id="element-135">
            "En menos de una semana aplicando el Intercambio Inteligente, los
            usuarios reportan la desaparición de la somnolencia post-comida (el
            famoso 'mal del puerco') y una estabilización de los niveles de
            glucosa en ayunas, simplemente por eliminar el impacto del maíz
            procesado en las cenas."
          </p>
          <div className="flex justify-center items-center space-x-2 text-orange-400" data-id="element-136">
            {[...Array(5)].map((_, i) => <CheckCircleIcon key={i} className="h-6 w-6" fill="currentColor" data-id="element-137" />)}
          </div>
          <p className="mt-4 font-bold text-lg text-stone-100" data-id="element-138">
            Resultados comprobados por nuestra comunidad
          </p>
        </div>

        <div className="mt-12" data-id="element-139">
          <ScrollButton data-id="element-140" />
        </div>
      </div>
    </section>;
}
function FAQSection() {
  const faqs = [{
    q: '¿Necesito ingredientes caros?',
    a: 'No, la mayoría se encuentra en cualquier mercado local (nopales, queso, chiles, verduras de temporada).'
  }, {
    q: '¿Es difícil de cocinar?',
    a: 'En absoluto. Si sabes calentar un comal y licuar una salsa, puedes seguir este protocolo sin problema.'
  }, {
    q: '¿Mi familia notará la diferencia?',
    a: "Probablemente prefieran tus nuevas 'costras de queso' a la versión original. Es comida real y deliciosa."
  }, {
    q: '¿Puedo salir a restaurantes?',
    a: 'Sí, el capítulo 5 está diseñado específicamente para sobrevivir en la calle y saber qué pedir en cualquier puesto o restaurante.'
  }];
  return <section className="py-16 md:py-24 bg-stone-50" data-id="element-141">
      <div className="max-w-4xl mx-auto px-5 md:px-8" data-id="element-142">
        <div className="text-center mb-12" data-id="element-143">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4" data-id="element-144">
            Preguntas Frecuentes
          </h2>
        </div>

        <div className="space-y-4" data-id="element-145">
          {faqs.map((faq, idx) => <details key={idx} className="group bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden" data-id="element-146">
              <summary className="flex justify-between items-center font-bold cursor-pointer list-none p-6 text-lg text-stone-800 hover:text-orange-700 transition-colors" data-id="element-147">
                {faq.q}
                <span className="transition group-open:rotate-180" data-id="element-148">
                  <ChevronDownIcon className="h-5 w-5" data-id="element-149" />
                </span>
              </summary>
              <div className="text-stone-600 p-6 pt-0 leading-relaxed border-t border-stone-100 mt-2" data-id="element-150">
                {faq.a}
              </div>
            </details>)}
        </div>
      </div>
    </section>;
}
function PricingSection() {
  const handleBuyClick = () => {
    if (window.fbq) window.fbq('track', 'AddToCart');
  };
  return <section id="comprar" className="py-16 md:py-24 bg-gradient-to-b from-stone-100 to-stone-200 relative" data-id="element-151">
      <div className="max-w-5xl mx-auto px-5 md:px-8 relative z-10" data-id="element-152">
        <div className="mb-8 flex justify-center" data-id="element-153">
          <LiveVisitorCounter data-id="element-154" />
        </div>

        <div className="text-center mb-12" data-id="element-155">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-stone-900 mb-6" data-id="element-156">
            Recupera tu lugar en la mesa familiar hoy mismo
          </h2>
          <p className="text-lg md:text-xl text-stone-600 max-w-2xl mx-auto mb-8" data-id="element-157">
            Obtén el sistema completo por una fracción de lo que cuesta una sola
            consulta con el nutricionista.
          </p>
          <CountdownBadge data-id="element-158" />
        </div>

        <div className="max-w-lg mx-auto" data-id="element-159">
          <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} className="bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-orange-100" data-id="element-160">
            <div className="bg-emerald-900 p-8 text-white text-center relative overflow-hidden" data-id="element-161">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-800 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-162"></div>
              <h3 className="text-2xl md:text-3xl font-serif font-bold mb-2 relative z-10" data-id="element-163">
                EL PROTOCOLO DE LA ANTOJERÍA DIABÉTICA
              </h3>
              <p className="text-emerald-200 font-medium relative z-10" data-id="element-164">
                Acceso digital inmediato
              </p>
            </div>

            <div className="p-8 md:p-10" data-id="element-165">
              <div className="flex flex-col justify-center items-center mb-8" data-id="element-166">
                <span className="text-stone-400 text-xl line-through mb-1" data-id="element-167">
                  Precio Normal: $54 USD
                </span>
                <div className="flex items-baseline text-orange-700" data-id="element-168">
                  <span className="text-5xl md:text-6xl font-bold tracking-tight" data-id="element-169">
                    $11.99
                  </span>
                  <span className="text-xl font-bold ml-2" data-id="element-170">USD</span>
                </div>
              </div>

              <div className="bg-orange-50 border border-orange-200 p-4 mb-8 rounded-xl flex items-start" data-id="element-171">
                <ClockIcon className="h-6 w-6 text-orange-600 mr-3 flex-shrink-0" data-id="element-172" />
                <p className="text-stone-800 font-medium text-sm leading-snug" data-id="element-173">
                  <span className="font-bold text-orange-700" data-id="element-174">
                    Precio Especial de Lanzamiento.
                  </span>{' '}
                  La oferta expira pronto.
                </p>
              </div>

              <a href="https://pay.hotmart.com/D105521668V" onClick={handleBuyClick} className="block w-full bg-orange-600 hover:bg-orange-700 text-white text-center font-bold px-6 py-5 rounded-xl text-lg md:text-xl transition-all duration-300 shadow-[0_10px_20px_rgba(234,88,12,0.3)] transform hover:-translate-y-1 active:translate-y-0" data-id="element-175">
                ¡QUIERO VOLVER A COMER RICO! <br data-id="element-176" />
                <span className="text-sm font-normal opacity-90 mt-1 block" data-id="element-177">
                  — Solo $11.99 USD —
                </span>
              </a>

              <ul className="mt-8 space-y-4" data-id="element-178">
                <li className="flex items-center text-stone-600 font-medium" data-id="element-179">
                  <CheckCircleIcon className="h-5 w-5 mr-3 text-emerald-500" data-id="element-180" />{' '}
                  Acceso inmediato tras el pago
                </li>
                <li className="flex items-center text-stone-600 font-medium" data-id="element-181">
                  <CheckCircleIcon className="h-5 w-5 mr-3 text-emerald-500" data-id="element-182" />{' '}
                  Formato digital (PDF)
                </li>
                <li className="flex items-center text-stone-600 font-medium" data-id="element-183">
                  <CheckCircleIcon className="h-5 w-5 mr-3 text-emerald-500" data-id="element-184" />{' '}
                  Compatible con celular, tablet y PC
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function RecentPurchaseNotification() {
  const purchases = [{
    name: 'María',
    location: 'CDMX',
    time: '2 minutos'
  }, {
    name: 'Alejandro',
    location: 'Guadalajara',
    time: '5 minutos'
  }, {
    name: 'Guadalupe',
    location: 'Monterrey',
    time: '1 minuto'
  }, {
    name: 'Juan',
    location: 'Puebla',
    time: '30 segundos'
  }, {
    name: 'Carmen',
    location: 'Tijuana',
    time: '3 minutos'
  }, {
    name: 'Roberto',
    location: 'Mérida',
    time: '4 minutos'
  }];
  const [visible, setVisible] = useState(false);
  const [currentPurchase, setCurrentPurchase] = useState(purchases[0]);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPurchase(purchases[Math.floor(Math.random() * purchases.length)]);
      setVisible(true);
      setTimeout(() => setVisible(false), 5000);
    }, 15000);
    return () => clearInterval(interval);
  }, []);
  return <div className={`fixed bottom-6 left-4 md:left-6 z-50 w-[90%] max-w-[320px] bg-white rounded-xl shadow-2xl border border-stone-200 transition-all duration-700 ease-in-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`} data-id="element-185">
      <div className="p-4 flex items-start" data-id="element-186">
        <div className="bg-emerald-100 p-2 rounded-full mr-3 mt-1" data-id="element-187">
          <CheckIcon className="h-4 w-4 text-emerald-600" data-id="element-188" />
        </div>
        <div className="flex-1" data-id="element-189">
          <div className="flex justify-between items-center mb-1" data-id="element-190">
            <span className="text-xs font-bold text-stone-500 uppercase tracking-wider" data-id="element-191">
              Nueva Compra
            </span>
            <span className="text-xs text-stone-400" data-id="element-192">
              Hace {currentPurchase.time}
            </span>
          </div>
          <p className="text-sm text-stone-800 font-medium" data-id="element-193">
            <span className="font-bold" data-id="element-194">{currentPurchase.name}</span> de{' '}
            {currentPurchase.location} acaba de comprar el protocolo.
          </p>
        </div>
      </div>
    </div>;
}
function LegalModal({
  isOpen,
  onClose,
  title,
  content
}: any) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-stone-900/80 backdrop-blur-sm" data-id="element-195">
      <motion.div initial={{
      scale: 0.95,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} className="relative w-full max-w-2xl max-h-[90vh] overflow-auto bg-white rounded-2xl shadow-2xl" data-id="element-196">
        <div className="sticky top-0 bg-white border-b border-stone-100 p-5 flex justify-between items-center z-10" data-id="element-197">
          <h2 className="text-xl font-serif font-bold text-stone-900" data-id="element-198">
            {title}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-stone-100 transition-colors" data-id="element-199">
            <XIcon className="h-6 w-6 text-stone-500" data-id="element-200" />
          </button>
        </div>
        <div className="p-6 md:p-8 text-stone-600 text-sm leading-relaxed" data-id="element-201">
          {content}
        </div>
      </motion.div>
    </div>;
}
function Footer() {
  const [activeModal, setActiveModal] = useState<string | null>(null);
  return <footer className="bg-stone-900 text-stone-400 py-12 border-t border-stone-800" data-id="element-202">
      <div className="max-w-7xl mx-auto px-5 md:px-8 text-center" data-id="element-203">
        <div className="text-white font-serif font-bold text-2xl mb-4 flex justify-center items-center" data-id="element-204">
          EL PROTOCOLO <LeafIcon className="ml-2 h-5 w-5 text-emerald-500" data-id="element-205" />
        </div>
        <p className="text-sm mb-6 max-w-md mx-auto" data-id="element-206">
          Transformando la manera en que los mexicanos disfrutan su comida
          tradicional, sin sacrificar su salud.
        </p>
        <p className="text-sm text-stone-500" data-id="element-207">
          © {new Date().getFullYear()} Todos los derechos reservados.
        </p>

        <div className="mt-6 flex justify-center space-x-6 text-sm font-medium" data-id="element-208">
          <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors" data-id="element-209">
            Términos y Condiciones
          </button>
          <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors" data-id="element-210">
            Política de Privacidad
          </button>
        </div>
      </div>

      <LegalModal isOpen={activeModal !== null} onClose={() => setActiveModal(null)} title={activeModal === 'terms' ? 'Términos y Condiciones' : 'Política de Privacidad'} content={<div className="space-y-4" data-id="element-212">
            <p data-id="element-213">
              Este producto no sustituye el consejo médico profesional,
              diagnóstico o tratamiento. Siempre busque el consejo de su médico
              u otro proveedor de salud calificado con cualquier pregunta que
              pueda tener respecto a una condición médica.
            </p>
            <p data-id="element-214">
              Los resultados pueden variar de persona a persona. Al adquirir "El
              Protocolo de la Antojería Diabética", aceptas utilizarlo para
              fines personales y no redistribuir su contenido.
            </p>
            <p data-id="element-215">
              Implementamos medidas de seguridad para proteger tu información.
              No compartimos datos con terceros sin tu consentimiento expreso.
            </p>
          </div>} data-id="element-211" />
    </footer>;
}
export function App() {
  return <div className="w-full min-h-screen bg-stone-50 font-sans selection:bg-orange-200 selection:text-orange-900" data-id="element-216">
      <MetaPixel data-id="element-217" />
      <Header data-id="element-218" />

      <main data-id="element-219">
        <div className="max-w-7xl mx-auto px-5 md:px-8 mt-6 flex justify-center" data-id="element-220">
          <LiveVisitorCounter data-id="element-221" />
        </div>

        <HeroSection data-id="element-222" />
        <ProblemSection data-id="element-223" />
        <UniqueMethodSection data-id="element-224" />
        <WhatYouGetSection data-id="element-225" />
        <WhoIsThisForSection data-id="element-226" />
        <ProofSection data-id="element-227" />
        <FAQSection data-id="element-228" />
        <PricingSection data-id="element-229" />
      </main>

      <Footer data-id="element-230" />
      <RecentPurchaseNotification data-id="element-231" />
    </div>;
}